import React, { useCallback, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";

import classnames from "classnames";

import { currenciesArray } from "../../../constants";
import { DropdownProps, ObjectValue, OnChangeType } from "./dropdown-types";
import { DropdownBody } from "./DropdownBody";
import { DropdownHead } from "./DropdownHead";

import "./Dropdown.styles.scss";

export const Dropdown: React.FC<DropdownProps & { style?: React.CSSProperties }> = ({
  value = [],
  onChange,
  theme = "standard",
  options = theme === "currency" ? currenciesArray : [],
  multiSelect,
  label,
  disabled,
  required,
  errors,
  className = "",
  placeholder = "Choose Option",
  loading,
  style
}): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  type IsStringType = (value: string | ObjectValue) => boolean;
  const isString: IsStringType = useCallback((value: string | ObjectValue) => typeof value === "string", []);

  type OnClickOutsideType = () => void;
  const onClickOutside: OnClickOutsideType = useCallback(() => setIsOpen(false), []);

  const multiSelectHandler: OnChangeType = useCallback(
    (selected: string | ObjectValue) => {
      return isString(selected)
        ? (value as string[]).includes(selected as string)
          ? onChange?.((value as string[]).filter((el: string) => el !== selected))
          : onChange?.([...(value as string[]), selected as string])
        : (value as ObjectValue[])?.findIndex((el: ObjectValue) => el.id === (selected as ObjectValue).id) !== -1
          ? onChange?.((value as ObjectValue[]).filter((el: ObjectValue) => el.id !== (selected as ObjectValue).id))
          : onChange?.([...(value as ObjectValue[]), selected as ObjectValue]);
    },
    [value]
  );

  const onChangeHandler: OnChangeType = useCallback(
    async (selected) => {
      if (disabled || loading || !onChange) {
        return;
      }
      if (multiSelect) {
        multiSelectHandler(selected);
      } else {
        await onChange(selected);
        setIsOpen(false);
      }
    },
    [multiSelect, value, loading, disabled, onChange]
  );

  return (
    <div
      className={classnames("dropdown", `dropdown--${theme}`, {
        "dropdown--disabled": disabled,
        [className]: className
      })}
      style={style}
    >
      {label ? (
        <span
          className={classnames("label", {
            "label--required": required,
            "label--disabled": disabled
          })}
        >
          {label}
        </span>
      ) : null}
      <OutsideClickHandler onOutsideClick={onClickOutside}>
        <DropdownHead
          value={value}
          isOpen={isOpen}
          setIsOpen={options?.length ? setIsOpen : undefined}
          theme={theme}
          disabled={disabled}
          errors={errors}
          placeholder={placeholder}
          loading={loading}
        />
        <DropdownBody onChange={onChangeHandler} options={options} value={value} isOpen={isOpen} theme={theme} />
      </OutsideClickHandler>
    </div>
  );
};
